import '@angular/compiler'
import packageJson from '../package.json'
import { ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from '@angular/core'
import { provideRouter, Router, withRouterConfig } from '@angular/router'
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http'
import { provideApollo } from 'apollo-angular'
import { apolloProvider } from './app/apollo.provider'
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser'
import { MomentModule } from 'ngx-moment'
import { EffectsModule } from '@ngrx/effects'
import { ActionReducer, StoreModule } from '@ngrx/store'
import { init, TraceService, createErrorHandler, replayIntegration, browserTracingIntegration } from '@sentry/angular'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { localStorageSync } from 'ngrx-store-localstorage'
import { AppComponent } from './app/app.component'
import { reducers } from './app/store'
import {
  UserEffects,
  OrganizationEffects,
  MapsEffects,
  MapExportEffects,
  SearchEffects,
  BasemapEffects,
  LayersEffects,
} from './app/store/effects'
import { appRoutes } from './app/app.routes'
import { RequestInterceptorService } from './app/auth.interceptor'
import { MARKED_OPTIONS, MarkedOptions, MarkedRenderer, provideMarkdown } from 'ngx-markdown'
import { provideMapboxGL } from 'ngx-mapbox-gl'
import { environment } from '@environment'
import { STATSIG_INIT_CONFIG, StatsigService as StatsigAngularService } from '@statsig/angular-bindings'

init({
  allowUrls: [/https?:\/\/((app)\.)?ziptility\.com/],
  dsn: 'https://ed607d42fa754f2cb90ec3e5b4ee8c2d@o426635.ingest.sentry.io/5369695',
  environment: environment.name,
  integrations: [
    browserTracingIntegration(),
    replayIntegration({
      stickySession: true,
    }),
  ],
  release: packageJson.version,
  tracesSampleRate: 0.2,
  tracePropagationTargets: [
    /^https:\/\/api\.ziptility\.com/,
    /^https:\/\/staging\.api\.ziptility\.com/,
    /^https:\/\/dev\.api\.ziptility\.com/,
    /^http:\/\/localhost/,
  ],
  replaysOnErrorSampleRate: 1.0,
})

function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({ keys: ['preference', 'measure'], rehydrate: true })(reducer)
}

export function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer()
  return { renderer }
}

const ZipAPIInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  if (req.url.startsWith('http')) {
    return next(req)
  }
  const apiReq = req.clone({ url: `${environment.apiBase}${req.url}` })
  return next(apiReq)
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      MomentModule,
      EffectsModule.forRoot([
        UserEffects,
        OrganizationEffects,
        MapsEffects,
        MapExportEffects,
        SearchEffects,
        BasemapEffects,
        LayersEffects,
      ]),
      StoreModule.forRoot(reducers, {
        metaReducers: [localStorageSyncReducer],
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
        },
      }),
      MatDatepickerModule,
      MatNativeDateModule,
    ),
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptorService, multi: true },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
      }),
    },
    provideMapboxGL({
      accessToken: environment.mapboxToken,
    }),
    provideMarkdown({
      loader: HttpClient,
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
    }),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([ZipAPIInterceptor])),
    provideApollo(apolloProvider),
    provideRouter(appRoutes, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    {
      provide: STATSIG_INIT_CONFIG,
      useValue: {
        sdkKey: environment.statsigClientKey || '',
        user: {},
      },
    },
    {
      provide: StatsigAngularService,
      useClass: StatsigAngularService,
      deps: [STATSIG_INIT_CONFIG],
    },
  ],
})
